import {
  init as sentryInit,
  browserTracingIntegration,
  replayIntegration,
  captureException as sentryCaptureException,
} from '@sentry/react';
import {
  configureDatadog,
  getDatadogLogger,
  type Environment as DatadogEnvironment,
} from '@storebrand-digital/common-datadog';

import {
  extractFromFetchError,
  getDatadogMessageContext,
  getSentryHint,
} from './fetchError';
import { getErrorMessage } from './getErrorMessage';
import { sanitizeLogContext } from './reservedAttributes';
import type {
  AppEnvironment,
  Logger,
  LoggerOptions,
  MonitoringUser,
} from './types';

/**
 * Maps a deployed app environment to the human-readable label shared across
 * every monitoring transport (events, Datadog, Sentry). `dev` and `offline` are
 * excluded — the logger short-circuits to no-ops locally and never needs a
 * label.
 */
const ENVIRONMENT_LABEL: Record<
  Exclude<AppEnvironment, 'dev' | 'offline'>,
  DatadogEnvironment
> = {
  prod: 'production',
  test: 'client-t',
};

/** Shared no-op transport used by the local-dev logger. */
const noop = (): void => undefined;

/**
 * Console fallback for events. Only used when a *deployed* environment cannot
 * reach Datadog (transport disabled, or the send threw) — local dev stays
 * silent. Monitoring transports are remote and fail silently, so a dropped
 * event must at least leave a trace in the browser.
 */
const logEventToConsole = (
  event: string,
  data: Record<string, unknown>,
): void => {
  console.info(`[event] ${event}`, data);
};

/**
 * Create a logger wired to the monitoring transports you enable.
 *
 * Datadog and Sentry are independent — pass `datadog`, `sentry`, both, or
 * neither. With neither enabled the logger only writes to the browser console.
 *
 * Each transport is enabled by passing its config object (which the logger also
 * uses to own SDK initialisation: call the returned `initMonitoring(user)` once
 * at bootstrap; each transport is initialised at most once, and a failed init
 * can be retried). When `environment` is `dev` no SDK is initialised and events
 * are dropped (only `logError` reaches the console), so call sites can pass
 * their configs unconditionally.
 *
 * Event attributes are sanitised against Datadog's reserved attributes, so a
 * `status`/`message` key cannot rewrite the log's level or message.
 *
 * @example
 * export const { logError, logEvent, logInfo, initMonitoring } = createLogger({
 *   environment: getEnv(),
 *   datadog: {
 *     appId: window.env.DATADOG_APP_ID,
 *     appName: window.env.CLIENT_ID,
 *     appVersion: artifactInfo.packageVersion,
 *     clientPublicToken: window.env.DATADOG_PUBLIC_TOKEN,
 *   },
 *   sentry: {
 *     dsn: window.env.SENTRY_DSN,
 *     release: __APP_VERSION__,
 *     tracesSampleRate: isProdEnv ? 0.25 : 1.0,
 *   },
 * });
 */
export const createLogger = ({
  environment,
  datadog,
  sentry,
}: LoggerOptions): Logger => {
  // Local dev and offline (E2E) builds: never initialise a monitoring SDK, and
  // stay quiet. Errors are still surfaced in the browser console; events are
  // not, so local work isn't drowned in diagnostics meant for Datadog.
  if (environment === 'dev' || environment === 'offline') {
    return {
      logError: (error: unknown): void => {
        console.error(error ?? getErrorMessage(error));
      },
      logEvent: noop,
      logEntry: noop,
      logInfo: noop,
      initMonitoring: noop,
    };
  }

  const datadogEnabled = datadog !== undefined;
  const sentryEnabled = sentry !== undefined;
  const environmentLabel = ENVIRONMENT_LABEL[environment];

  // The Datadog and Sentry SDKs are global singletons, so each is initialised at
  // most once. Tracked per transport and flipped only *after* a successful
  // init: a bootstrap that catches the throw and retries (as the apps do) must
  // be able to complete the initialisation it failed, without re-running the
  // one that already succeeded.
  let datadogInitialized = false;
  let sentryInitialized = false;

  const logError = (error: unknown): void => {
    const message = getErrorMessage(error);

    // Always surface the error in the browser for local debugging.
    console.error(error ?? message);

    if (!datadogEnabled && !sentryEnabled) {
      return;
    }

    try {
      const info = extractFromFetchError(error);

      if (datadogEnabled) {
        getDatadogLogger().error(
          message,
          getDatadogMessageContext(info),
          error instanceof Error ? error : undefined,
        );
      }

      if (sentryEnabled) {
        sentryCaptureException(error ?? message, getSentryHint(info));
      }
    } catch (e) {
      console.error(
        `Sending exception to monitoring platform failed: ${getErrorMessage(e)}`,
      );
    }
  };

  const logEvent = (
    event: string,
    data: Record<string, unknown> = {},
  ): void => {
    if (!datadogEnabled) {
      logEventToConsole(event, data);
      return;
    }

    try {
      getDatadogLogger().info(event, {
        ...sanitizeLogContext(data),
        environment: environmentLabel,
      });
    } catch (e) {
      logEventToConsole(event, data);
      console.error(
        `Sending event to monitoring platform failed: ${getErrorMessage(e)}`,
      );
    }
  };

  const logEntry = (data: Record<string, unknown> = {}): void => {
    logEvent('event:entered_solution', { ...data });
  };

  const logInfo = (message: string): void => {
    if (!datadogEnabled) {
      console.info(message);
      return;
    }

    try {
      getDatadogLogger().info(message, { environment: environmentLabel });
    } catch {
      // Same fallback as logEvent: a throwing transport must not make the
      // message disappear, which is the failure mode this logger exposes.
      console.info(message);
    }
  };

  const initMonitoring = ({ id, cmid }: MonitoringUser = {}): void => {
    // Sentry only counts a session as a user when the object carries `id`,
    // `email`, `username` or `ip_address`; anything else lands in `user.data`.
    // Omitted when both are unknown, so Sentry's own fallback still applies.
    const sentryUser =
      id !== undefined || cmid !== undefined ? { id, cmid } : undefined;

    // The transports are independent, so a failing one must not stop the other
    // from being set up. The first failure is rethrown once both were tried,
    // preserving the caller's error handling.
    let failure: unknown;

    if (datadog && !datadogInitialized) {
      try {
        configureDatadog({
          appId: datadog.appId,
          appName: datadog.appName,
          appVersion: datadog.appVersion,
          clientPublicToken: datadog.clientPublicToken,
          configureLogging: datadog.configureLogging ?? true,
          environment: environmentLabel,
          user: { cmid },
        });
        datadogInitialized = true;
      } catch (e) {
        failure = e;
      }
    }

    if (sentry && !sentryInitialized) {
      try {
        const replayEnabled =
          sentry.replaysSessionSampleRate !== undefined ||
          sentry.replaysOnErrorSampleRate !== undefined;

        sentryInit({
          dsn: sentry.dsn,
          environment: environmentLabel,
          integrations: replayEnabled
            ? [browserTracingIntegration(), replayIntegration()]
            : [browserTracingIntegration()],
          release: sentry.release,
          tracesSampleRate: sentry.tracesSampleRate ?? 1.0,
          replaysSessionSampleRate: sentry.replaysSessionSampleRate,
          replaysOnErrorSampleRate: sentry.replaysOnErrorSampleRate,
          initialScope: sentryUser ? { user: sentryUser } : undefined,
        });
        sentryInitialized = true;
      } catch (e) {
        failure ??= e;
      }
    }

    if (failure !== undefined) {
      throw failure;
    }
  };

  return { logError, logEvent, logEntry, logInfo, initMonitoring };
};
